import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { onMessage } from 'firebase/messaging';
import { auth, messaging } from './firebase';

import Header from './components/Header';
import RouteAdminPopupAd from './components/ads/RouteAdminPopupAd';
import RouteBottomAds from './components/ads/RouteBottomAds';

import AdminPage from './pages/admin/AdminPage';
import ManageRosterPage from './components/roster/ManageRosterPage';
import EditPrices from './pages/EditPrices';
import AddUser from './pages/admin/AddUser';
import PremiumUsers from './pages/admin/PremiumUsers';
import AddTeam from './pages/admin/AddTeam';
import AddNews from './pages/admin/AddNews';
import AddResponse from './pages/AddResponse';

import Home from './pages/Home';
import Wrestlers from './pages/Wrestlers';
import WrestlerDetails from './pages/WrestlerDetails';
import AddWrestler from './pages/admin/AddWrestler';
import BonusDetails from './pages/BonusDetails';
import Statistiche from './pages/Statistiche';
import NewsPage from './pages/NewsPage';
import AllLinks from './pages/AllLinks';

import Factions from './pages/Factions';
import FactionDetails from './pages/FactionDetails';
import FactionMarketPage from './pages/FactionMarketPage';
import FactionLineupPage from './pages/FactionLineupPage';
import FactionMatchesPage from './pages/FactionMatchesPage';
import AddFaction from './pages/admin/AddFaction';

import Shows from './pages/Shows';
import NextShows from './pages/NextShows';
import ShowDetails from './pages/ShowDetails';
import AddShow from './pages/admin/AddShow';
import EditShow from './pages/EditShow';

import Ranking from './pages/Ranking';
import Rules from './pages/Rules';
import Faq from './pages/Faq';
import Poll from './pages/Poll';
import Contatti from './pages/Contatti';
import About from './pages/About';
import Premium from './pages/Premium';
import Privacy from './pages/Privacy';

import RosterDetails from './pages/RosterDetails';
import AddRoster from './pages/admin/AddRoster';
import RosterInvite from './pages/RosterInvite';

import Auth from './auth/Auth';
import Profile from './pages/Profile';
import UserPage from './pages/UserPage';
import {
  buildAbsoluteNotificationLink,
  enablePushNotifications,
  ensureMessagingServiceWorkerRegistration,
  getNotificationPermissionState,
  isMessagingSupportedInBrowser,
  markNotificationPromptDismissed,
  shouldShowNotificationPrompt,
} from './services/notificationService';

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

function App() {
  const [user, setUser] = useState(null);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [notificationPromptLoading, setNotificationPromptLoading] = useState(false);
  const [notificationPromptError, setNotificationPromptError] = useState('');
  const [foregroundNotification, setForegroundNotification] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if ('serviceWorker' in navigator) {
      ensureMessagingServiceWorkerRegistration()
        .then((registration) => {
          console.log('Service Worker registrato con successo:', registration);
        })
        .catch((error) => {
          console.error('Errore nella registrazione del Service Worker:', error);
        });
    }
  }, []);

  useEffect(() => {
    let unsubscribeFromMessages = () => {};
    let isMounted = true;

    const setupForegroundNotifications = async () => {
      const supported = await isMessagingSupportedInBrowser();
      if (!supported || !isMounted) return;

      unsubscribeFromMessages = onMessage(messaging, (payload) => {
        if (getNotificationPermissionState() !== 'granted') return;

        const title = payload?.data?.title;
        const body = payload?.data?.body || '';
        if (!title) return;

        const link =
          payload?.data?.link ||
          payload?.fcmOptions?.link ||
          buildAbsoluteNotificationLink('/news');
        const notificationKey = payload?.data?.notificationKey || `${title}:${link}`;

        setForegroundNotification({
          title,
          body,
          link,
          notificationKey,
          receivedAt: Date.now(),
        });
      });
    };

    setupForegroundNotifications();

    return () => {
      isMounted = false;
      unsubscribeFromMessages();
    };
  }, []);

  useEffect(() => {
    if (!foregroundNotification) return undefined;

    const timeoutId = window.setTimeout(() => {
      setForegroundNotification((current) =>
        current?.receivedAt === foregroundNotification.receivedAt ? null : current
      );
    }, 8000);

    return () => window.clearTimeout(timeoutId);
  }, [foregroundNotification]);

  useEffect(() => {
    let ignore = false;

    const syncGrantedPermission = async () => {
      if (!user || getNotificationPermissionState() !== 'granted') return;

      try {
        await enablePushNotifications({ user, skipPermissionRequest: true });
      } catch (error) {
        if (!ignore) {
          console.warn('Sincronizzazione token notifiche non riuscita:', error);
        }
      }
    };

    syncGrantedPermission();

    return () => {
      ignore = true;
    };
  }, [user]);

  useEffect(() => {
    setNotificationPromptError('');
    setShowNotificationPrompt(shouldShowNotificationPrompt(user));
  }, [user]);

  const handleEnableNotifications = async () => {
    setNotificationPromptLoading(true);
    setNotificationPromptError('');

    try {
      const result = await enablePushNotifications({ user });
      if (!result.enabled) {
        markNotificationPromptDismissed();
      }
      setShowNotificationPrompt(false);
    } catch (error) {
      setNotificationPromptError(error.message || 'Non sono riuscito ad attivare le notifiche.');
    } finally {
      setNotificationPromptLoading(false);
    }
  };

  const handleDismissNotificationPrompt = () => {
    markNotificationPromptDismissed();
    setNotificationPromptError('');
    setShowNotificationPrompt(false);
  };

  return (
    <Router>
      <ScrollToTop />
      <Header user={user} />

      <main className="mt-5 pt-4 mb-2 py-5">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/rules" element={<Rules />} />
          <Route path="/wrestlers" element={<Wrestlers />} />
          <Route path="/wrestlers/:id" element={<WrestlerDetails />} />
          <Route path="/ranking" element={<Ranking />} />
          <Route path="/factions" element={<Factions />} />
          <Route path="/factions/:id" element={<FactionDetails />} />
          <Route path="/factions/:id/market" element={<FactionMarketPage />} />
          <Route path="/factions/:id/lineup" element={<FactionLineupPage />} />
          <Route path="/factions/:id/matches" element={<FactionMatchesPage />} />
          <Route path="/shows" element={<Shows />} />
          <Route path="/next-shows" element={<NextShows />} />
          <Route path="/shows/:id" element={<ShowDetails />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/rosters/:id" element={<RosterDetails />} />
          <Route path="/invite/:rosterId" element={<RosterInvite />} />

          <Route path="/auth" element={<Auth />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/users/:id" element={<UserPage />} />
          <Route path="/poll" element={<Poll />} />
          <Route path="/contatti" element={<Contatti />} />
          <Route path="/about" element={<About />} />
          <Route path="/premium" element={<Premium />} />
          <Route path="/donazioni" element={<Navigate to="/premium" replace />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/all-links" element={<AllLinks />} />

          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/add-wrestlers" element={<AddWrestler />} />
          <Route path="/admin/add-teams" element={<AddTeam />} />
          <Route path="/admin/add-shows" element={<AddShow />} />
          <Route path="/admin/add-news" element={<AddNews />} />
          <Route path="/shows/:id/edit-show" element={<EditShow />} />
          <Route path="/admin/edit-prices" element={<EditPrices />} />
          <Route path="/admin/add-factions" element={<AddFaction />} />
          <Route path="/admin/add-roster" element={<AddRoster />} />
          <Route path="/admin/users" element={<AddUser />} />
          <Route path="/admin/premium-users" element={<PremiumUsers />} />
          <Route path="/admin/add-response" element={<AddResponse />} />
          <Route path="/roster/manage/:rosterId" element={<ManageRosterPage />} />
          <Route path="/bonus/:bonusValue" element={<BonusDetails />} />
          <Route path="/stats" element={<Statistiche />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="*" element={<h2 className="text-center mt-5">404 - Pagina non trovata</h2>} />
        </Routes>
        <RouteAdminPopupAd user={user} />
        <RouteBottomAds />
      </main>

      {foregroundNotification && (
        <div className="app-notification-toast" role="status" aria-live="polite">
          <button
            type="button"
            className="app-notification-toast__content"
            onClick={() => {
              window.location.href = foregroundNotification.link;
            }}
          >
            <span className="app-notification-toast__icon">
              <i className="bi bi-bell-fill"></i>
            </span>
            <span className="app-notification-toast__copy">
              <strong>{foregroundNotification.title}</strong>
              {foregroundNotification.body && <span>{foregroundNotification.body}</span>}
            </span>
          </button>
          <button
            type="button"
            className="app-notification-toast__close"
            aria-label="Chiudi notifica"
            onClick={() => setForegroundNotification(null)}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>
      )}

      {showNotificationPrompt && (
        <div
          className="modal fade show d-block notification-permission-modal"
          tabIndex="-1"
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered notification-permission-modal__dialog">
            <div className="modal-content border-0 notification-permission-modal__content">
              <div className="modal-header border-0 notification-permission-modal__header">
                <div className="notification-permission-modal__hero">
                  <h5 className="modal-title notification-permission-modal__title">
                    <i className="bi bi-bell-fill"></i>
                    Attiva le notifiche
                  </h5>
                </div>
                <button
                  type="button"
                  className="btn-close btn-close-white notification-permission-modal__close"
                  aria-label="Chiudi"
                  onClick={handleDismissNotificationPrompt}
                  disabled={notificationPromptLoading}
                />
              </div>

              <div className="modal-body notification-permission-modal__body">
                <p className="notification-permission-modal__lead mb-3">
                  Attiva le notifiche per rimanere aggiornato.
                </p>

                <div className="notification-permission-modal__chips">
                  <span className="notification-permission-modal__chip">
                    <i className="bi bi-newspaper"></i>
                    News
                  </span>
                  <span className="notification-permission-modal__chip">
                    <i className="bi bi-trophy"></i>
                    Risultati show settimanali e PLE
                  </span>
                  <span className="notification-permission-modal__chip">
                    <i className="bi bi-lightning-charge"></i>
                    Avvisi importanti
                  </span>
                </div>

                <p className="notification-permission-modal__helper mb-0">
                  Dopo il tap su &quot;Attiva&quot;, il browser mostrera il popup ufficiale di autorizzazione.
                </p>

                {notificationPromptError && (
                  <div className="alert alert-danger mt-3 mb-0 notification-permission-modal__alert">
                    {notificationPromptError}
                  </div>
                )}
              </div>

              <div className="modal-footer border-0 notification-permission-modal__footer">
                <button
                  type="button"
                  className="btn btn-outline-brand-secondary notification-permission-modal__secondary-btn"
                  onClick={handleDismissNotificationPrompt}
                  disabled={notificationPromptLoading}
                >
                  Magari dopo
                </button>
                <button
                  type="button"
                  className="btn btn-brand-secondary notification-permission-modal__primary-btn"
                  onClick={handleEnableNotifications}
                  disabled={notificationPromptLoading}
                >
                  {notificationPromptLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Attivazione...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-bell me-1"></i>
                      Attiva
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Router>
  );
}

export async function requestNotificationPermission() {
  return enablePushNotifications();
}

export default App;
